import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgeCheckIcon, XIcon, MapPinIcon } from 'lucide-react';
const NAMES = ['Mariana', 'Rodrigo', 'Florencia', 'Lucas', 'Sofía', 'Martín', 'Camila', 'Diego', 'Valentina', 'Nicolás', 'Agustina', 'Joaquín', 'Carla', 'Federico', 'Julieta', 'Gonzalo', 'Brenda', 'Tomás', 'Daniela', 'Ezequiel', 'Romina', 'Matías', 'Paula', 'Santiago', 'Micaela', 'Facundo', 'Lucía', 'Ignacio'];
const SURNAME_INITIALS = ['L.', 'P.', 'M.', 'G.', 'R.', 'S.', 'F.', 'D.', 'C.', 'A.', 'B.', 'V.'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'San Miguel de Tucumán', 'Salta', 'Santa Fe', 'San Juan', 'Neuquén', 'Resistencia', 'Posadas', 'Bahía Blanca', 'Paraná', 'Corrientes', 'Quilmes'];
const OFFERS = ['el Kit Consultorio en Orden', 'la Edición Completa'];
const TIME_AGO = ['hace un momento', 'hace 1 minuto', 'hace 2 minutos', 'recién'];
interface Purchase {
  name: string;
  city: string;
  offer: string;
  timeAgo: string;
  key: number;
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function buildPurchase(): Purchase {
  return {
    name: `${pick(NAMES)} ${pick(SURNAME_INITIALS)}`,
    city: pick(CITIES),
    offer: pick(OFFERS),
    timeAgo: pick(TIME_AGO),
    key: Date.now()
  };
}
// Random interval between appearances: 40–60s
const nextDelay = () => 40000 + Math.random() * 20000;
// How long each toast stays visible
const VISIBLE_MS = 6000;
// First toast appears shortly after load
const INITIAL_DELAY = 8000;
export function PurchaseNotification() {
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const hide = useCallback(() => setPurchase(null), []);
  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    const show = () => {
      setPurchase(buildPurchase());
      hideTimer = setTimeout(() => {
        setPurchase(null);
        nextTimer = setTimeout(show, nextDelay());
      }, VISIBLE_MS);
    };
    const startTimer = setTimeout(show, INITIAL_DELAY);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
    };
  }, []);
  return <div aria-live="polite" className="pointer-events-none fixed bottom-4 left-4 z-50 max-w-[calc(100vw-2rem)] sm:bottom-6 sm:left-6" data-id="element-207">
      <AnimatePresence data-id="element-208">
        {purchase && <motion.div key={purchase.key} role="status" initial={{
        opacity: 0,
        y: 24,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 26
      }} className="pointer-events-auto flex w-[330px] max-w-full items-start gap-3 rounded-xl border border-brandGray-light bg-white p-3.5 shadow-[0_8px_32px_rgba(15,27,45,0.18)]" data-id="element-209">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#198754]/10 text-[#198754]" data-id="element-210">
              <BadgeCheckIcon className="h-6 w-6" strokeWidth={2} aria-hidden="true" data-id="element-211" />
            </span>

            <div className="min-w-0 flex-1" data-id="element-212">
              <div className="mb-0.5 flex items-center gap-1.5" data-id="element-213">
                <p className="font-montserrat text-[11px] font-extrabold uppercase tracking-[1px] text-[#198754]" data-id="element-214">
                  Compra verificada
                </p>
              </div>
              <p className="font-montserrat text-[13px] leading-snug text-navy" data-id="element-215">
                <span className="font-bold" data-id="element-216">{purchase.name}</span> compró{' '}
                <span className="font-bold" data-id="element-217">{purchase.offer}</span>
              </p>
              <div className="mt-1 flex items-center gap-1 text-brandGray" data-id="element-218">
                <MapPinIcon className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-219" />
                <p className="font-montserrat text-[11px]" data-id="element-220">
                  {purchase.city}, Argentina · {purchase.timeAgo}
                </p>
              </div>
            </div>

            <button onClick={hide} aria-label="Cerrar notificación" className="-mr-1 -mt-1 shrink-0 rounded p-1 text-brandGray transition-colors hover:bg-offWhite hover:text-navy" data-id="element-221">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-222" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}